import styled, { keyframes } from "styled-components";
import React, { useEffect, useCallback, useState, useRef } from "react";
import { Checkbox, Icon } from "semantic-ui-react";
import { useSelector, shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { SG_CREATE_DEBRIEF_PDF_REPORT } from "constants/actions";
import { v4 as uuidv4 } from "uuid";

const getPrimaryOptions = (categories) => {
  let categoryList = [];
  const parentCategory = categories.find(
    (category) => category.priority === "primary"
  );
  if (parentCategory) {
    parentCategory.options.forEach((option) => {
      categoryList.push({
        id: option.id,
        name: option.name,
      });
    });
  }

  return categoryList;
};

const ReportPrimaries = ({
  next,
  categories,
  parentCategory,
  reportType,
  get_auth,
  orgName,
  goBack,
}) => {
  const [primaryOptions, setPrimaryOptions] = useState([]);
  const [selectedPrimaries, setSelectedPrimaries] = useState([]);
  const [checkboxDisabled, setCheckboxDisabled] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { coreData } = useSelector(
    (state) => ({
      coreData: state.audit?.core_data,
    }),
    shallowEqual
  );

  const createReport = () => {
    const uuid = uuidv4();
    const report = {
      template_id: reportType?.id,
      organization: Number(get_auth?.organization_id),
      employee: Number(get_auth?.employee_id),
      tracking_code: uuid,
      params: {
        parent_id: parentCategory,
        parent_children: categories,
        primary_list: selectedPrimaries,
        orgName: orgName,
      },
    };

    dispatch({
      type: SG_CREATE_DEBRIEF_PDF_REPORT,
      payload: report,
    });

    navigate("/leader/audit/reports/recV4");
  };

  const handleCategorySelected = (id) => {
    if (selectedPrimaries.includes(id)) {
      setSelectedPrimaries(selectedPrimaries.filter((c) => c !== id));
    } else {
      setSelectedPrimaries([...selectedPrimaries, id]);
    }
  };

  useEffect(() => {
    if (coreData?.categories && coreData?.categories?.length > 0) {
      setPrimaryOptions(getPrimaryOptions(coreData.categories?.[0].categories));
    }
  }, [coreData]);

  const entireOrgSelect = () => {
    setSelectedPrimaries([]);
    setCheckboxDisabled(!checkboxDisabled);
  };

  return (
    <Container>
      <BackButton onClick={() => goBack()}>
        <Icon name="chevron left" style={{ marginTop: -7 }} />
        Back
      </BackButton>
      <T1>Select the report target</T1>
      <Description>
        Please select a target for your report. You'll have the opportunity to
        select additional options in the next step.
      </Description>

      <SelectArea>
        <Option
          title="Entire Organization"
          onSelect={() => entireOrgSelect()}
          checked={checkboxDisabled}
        />
        {primaryOptions.map((category) => {
          return (
            <Option
              checked={selectedPrimaries.includes(category.id)}
              key={category.id}
              id={category.id}
              title={category.name}
              disabled={checkboxDisabled}
              onSelect={(id) => handleCategorySelected(id)}
            />
          );
        })}
      </SelectArea>
      <ButtonContainer>
        <Button2 onClick={() => createReport()}>Generate Report</Button2>
      </ButtonContainer>
    </Container>
  );
};

const Option = ({ title, disabled, onSelect, checked, id }) => {
  return (
    <OptionContainer active={checked} disabled={disabled}>
      <Checkbox
        checked={checked}
        onChange={() => onSelect(id)}
        style={{ marginRight: 10 }}
        disabled={disabled}
      />{" "}
      {title}
    </OptionContainer>
  );
};

const fadeIn = keyframes`

    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const BackButton = styled.div`
  font-size: 14px;
  color: #b3b3b3;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 12px;
`;

const Container = styled.div``;

const T1 = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
  font-family: "Raleway";
  color: #2a3039;
  line-height: 1.5;
  margin-top: 20px;
  opacity: 0;
  animation: ${fadeIn} 1s ease-out forwards;
  letter-spacing: -0.35px;
`;

const Button2 = styled.div`
  color: #2d70e2;
  border: 1px solid #2d70e2;
  font-size: 16px;
  padding: 10px 20px;
  font-weight: 600;
  cursor: pointer;
  font-family: "Raleway";
`;

const Description = styled.div`
  font-size: 16px;
  color: #2a3039;
  margin-bottom: 20px;
  line-height: 1.4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: 0;
  animation: ${fadeIn} 1s ease-out forwards;
  animation-delay: 0.5s;
`;

const SelectArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 20px;
  width: 100%;
`;

const OptionContainer = styled.div`
  width: calc(33% - 5px);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-family: "Raleway";
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 5px;
  margin-right: 2px;
  margin-left: 2px;

  background-color: ${({ active }) => (active ? "#D5E2F9" : "#fff")};
  opacity: 0;
  color: ${({ disabled }) => (disabled ? "#ccc" : "#2a3039")};

  animation: ${fadeIn} 1s ease-out forwards;
  animation-delay: 1s;
`;

const ButtonContainer = styled.div`
    display:flex;
    margin-top:20px;
    justify-content:flex-end;
    align-items:center
    width:100%;

    opacity:0;

    animation: ${fadeIn} 1s ease-out forwards;
    animation-delay: 1s;

`;

const Button = styled.div`
  color: #2d70e2;
  border: 1px solid #2d70e2;
  font-size: 16px;
  padding: 10px 20px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  font-family: "Raleway";
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

export default ReportPrimaries;
